<template>
    <div>
        <vx-selectable
            :multiple="true"
            :no-tags="true"
            @search="search"
            placeholder="Select a segment"
            :options="options"
            v-model="segments"
            :clearable="true"
            :dropdownShouldOpen="dropdownShouldOpen"
            :deselectFromDropdown="true"
            :relativeDropdown="true"
            :closeOnSelect="false"
            :clearSearchOnSelect="false"
            @update:modelValue="emit('update:modelValue', $event)"
            :disabled="props.disabled"
            :size="props.size"
            :error="props.error"
        >
            <template v-slot:option="{ option }">
                <segments-selectable-row
                    :segment="option"
                    :contactsCount="contactsCount[option.id]"
                    :selected="isSelected(option)"
                >
                </segments-selectable-row>
            </template>
        </vx-selectable>

        <div
            v-if="segments.length"
            class="border border-solid border-slate-300 rounded-lg mt-6 grid grid-cols-[1fr_auto] items-center"
            :class="[currentOrgUnit?.id ? 'lg:grid-cols-[1fr_auto_auto]' : 'lg:grid-cols-[1fr_auto_auto_auto]']"
        >
            <div
                data-test="to-segments-selected"
                v-for="segment in segments"
                :key="segment.id"
                tabindex="0"
                role="button"
                class="appearance-none border-x-0 border-t-0 last:border-b-0 border-b border-slate-300 border-solid flex items-center justify-between px-4 text-slate-700 text-sm grid grid-cols-subgrid col-span-2"
                :class="[currentOrgUnit?.id ? 'lg:col-span-3' : 'lg:col-span-4']"
                @click="remove(segment)"
                @keydown.enter="remove(segment)"
                @keydown.space="remove(segment)"
            >
                <segments-selectable-row
                    :disabled="props.disabled"
                    :segment="segment"
                    :contactsCount="contactsCount[segment.id]"
                    :selected="isSelected(segment)"
                    class="py-2"
                >
                </segments-selectable-row>
            </div>
        </div>
    </div>
</template>

<script setup>
import { VxSelectable } from '@voxie/frontend-components';
import { segmentsClient } from '~/services';
import { ref, onMounted, watch, inject, computed } from 'vue';
import debounce from '~/utils/debounce';
import SegmentsSelectableRow from './SegmentsSelectableRow.vue';
import {
    AUDIENCE_TYPE_MARKETING, AUDIENCE_TYPE_TRANSACTIONAL, AUDIENCE_TYPE_ACQUISITION, AUDIENCE_TYPE_RE_ACQUISITION
} from '~/constants/campaign';
import { SegmentType } from '~/constants/segment';
import { useSegmentsContactsCount } from '~/composables/useSegmentsContactsCount';
import { useCurrentGroup } from '~/composables/useCurrentGroup';

const FEATURES = inject('FEATURES')

const teamId = Spark.state.currentTeam.id;

const props = defineProps({
    modelValue: {
        type: Array,
        required: false,
        default: () => [],
    },
    audienceType: {
        type: String,
        default: undefined,
        validator: (audienceType) => [AUDIENCE_TYPE_MARKETING, AUDIENCE_TYPE_TRANSACTIONAL, AUDIENCE_TYPE_ACQUISITION, AUDIENCE_TYPE_RE_ACQUISITION].includes(audienceType)
    },
    groupIds: {
     type: Array,
     required: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    size: {
        type: String,
        default: 'md'
    },
    flowId: {
        type: [String, undefined],
        default: undefined,
    },
    autoRefreshOnly: {
        type: Boolean,
        default: false,
    },
    error: String,
});

const emit = defineEmits(["update:modelValue"]);

watch(
    () => props.modelValue,
    (newModelValue) => {
        segments.value = newModelValue;
    }
);

watch(
    () => props.audienceType,
    () => {
        search();
    }
);

watch(
    () => props.groupIds,
    () => {
        search();
    }
);

const segments = ref(props.modelValue);
const { onCorporate, currentGroup, currentOrgUnit } = useCurrentGroup();
const { contactsCount, fetchSegmentsContactsCount } = useSegmentsContactsCount();

const options = ref([]);

const context = computed(() => ({ teamId, orgUnitId: currentOrgUnit.value?.id, groupId: currentGroup.value?.group_id }));

const isSelected = (segment) => {
    return Boolean(segments.value.find((item) => item.id == segment.id));
};

const remove = async (segment) => {
    if (props.disabled) {
        return;
    }

    segments.value = segments.value.filter((item) => item.id != segment.id);
    emit("update:modelValue", segments.value);
};

const getSegments = async (params) => {
    return await segmentsClient.getSegments(context.value, params);
}

// Fetching names is not a user edit, so only local state changes and nothing is emitted.
const fetchSelectedNames = async () => {
    const ids = segments.value.filter((segment) => !segment.name).map((segment) => Number(segment.id));
    if (!ids.length) {
        return;
    }

    const byId = new Map();
    try {
        const response = await getSegments({ filter: { ids } });
        response.data.data.forEach((segment) => byId.set(Number(segment.id), segment));
    } catch {
        // Fall through to the id-as-name fallback below.
    }
    segments.value = segments.value.map((segment) =>
        segment.name ? segment : byId.get(Number(segment.id)) ?? { ...segment, name: String(segment.id) }
    );
};

const search = debounce(async (search, loading) => {
    loading?.(true);

    const params = {
        per_page: 10,
        query: search,
        filter: {
            audience_type: props.audienceType,
            flow: FEATURES.flows ? {
                in_flow: props.flowId ? 1 : 0,
                flow_id: props.flowId,
            } : undefined
        },
    };

    if (onCorporate()) {
        params.filter.is_template = 0;
    }

    if (props.autoRefreshOnly) {
        params.filter.refresh_type = SegmentType.AUTO_REFRESH;
    }

    try {
        const response = await getSegments(params);
        options.value = response.data.data.map((segment) => {
            return {
                ...segment,
                label: `${segment.id} - ${segment.name}`,
            }
        });

        fetchSegmentsContactsCount(context.value, options.value.map(segment => segment.id), {
            group_ids: props.groupIds
        })
    } finally {
        loading?.(false);
    }
}, 300);

const dropdownShouldOpen = (VueSelect) => {
    return VueSelect.search.length > 0 || VueSelect.open;
};

onMounted(() => {
    // Load contacts count for selected segments
    if (props.modelValue.length > 0) {
        fetchSegmentsContactsCount(context.value, segments.value.map(segment => segment.id), {
            group_ids: props.groupIds
        });
    }

    fetchSelectedNames();

    // Do not load if disabled
    if (!props.disabled) {
        search();
    }
});

// for styles
const colsNumber = computed(() => (currentOrgUnit.value?.id ? 3 : 4));
</script>

<style scoped>
:deep(.vs__dropdown-menu) {
    display: grid !important;
    grid-template-columns: 1fr auto;
}

:deep(.vs__dropdown-option) {
    display: grid !important;
    grid-template-columns: subgrid;
    grid-column: span 2;
    column-gap: 0.5rem;
}

/* lg */
@media (min-width: 1024px) {
    :deep(.vs__dropdown-menu) {
        grid-template-columns: 1fr repeat(v-bind(colsNumber), auto);
    }

    :deep(.vs__dropdown-option) {
        grid-column: span v-bind(colsNumber);
    }
}
</style>
